/**
 * Som e vibração — sempre opcionais e desligáveis nas configurações da sala.
 */

let audioContext: AudioContext | null = null;

type AudioContextCtor = new () => AudioContext;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioContext) return audioContext;

  const scoped = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = scoped.AudioContext ?? scoped.webkitAudioContext;
  if (!Ctor) return null;

  try {
    audioContext = new Ctor();
    return audioContext;
  } catch {
    return null;
  }
}

/** O navegador só libera áudio depois de uma interação do usuário. */
export function unlockAudio(): void {
  const context = getAudioContext();
  if (context && context.state === 'suspended') void context.resume();
}

export type SoundName = 'play' | 'error' | 'tick' | 'win' | 'reward' | 'star';

const TONES: Record<SoundName, { frequency: number; duration: number; type: OscillatorType }> = {
  play: { frequency: 660, duration: 0.12, type: 'sine' },
  error: { frequency: 150, duration: 0.4, type: 'sawtooth' },
  tick: { frequency: 880, duration: 0.07, type: 'triangle' },
  win: { frequency: 880, duration: 0.5, type: 'sine' },
  reward: { frequency: 990, duration: 0.28, type: 'sine' },
  star: { frequency: 520, duration: 0.22, type: 'triangle' },
};

export function playSound(name: SoundName, enabled: boolean): void {
  if (!enabled) return;
  const context = getAudioContext();
  if (!context) return;
  if (context.state === 'suspended') void context.resume();

  try {
    const tone = TONES[name];
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = tone.type;
    oscillator.frequency.setValueAtTime(tone.frequency, context.currentTime);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + tone.duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + tone.duration + 0.02);
  } catch {
    // Áudio indisponível: seguimos em silêncio.
  }
}

export type HapticName = 'play' | 'error' | 'countdown' | 'win';

const PATTERNS: Record<HapticName, number | number[]> = {
  play: 12,
  error: [40, 60, 90],
  countdown: 8,
  win: [30, 50, 30, 50, 120],
};

export function vibrate(name: HapticName, enabled: boolean): void {
  if (!enabled) return;
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(PATTERNS[name]);
  } catch {
    // Alguns navegadores bloqueiam vibração sem gesto recente.
  }
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
